import type { GameState, ShareholderMandate, VictoryRecord } from './types';
import { updateAchievements } from './achievements';

export interface MilestoneDefinition {
  id: string;
  title: string;
  detail: string;
  achieved: (state: GameState) => boolean;
}

export interface CompanyScore {
  total: number;
  profitability: number;
  network: number;
  reputation: number;
  balanceSheet: number;
  resilience: number;
}

export type StrategicAlertTarget =
  | { kind: 'route'; routeId: string }
  | { kind: 'aircraft'; aircraftId: string }
  | { kind: 'finance'; tab: 'debt' | 'events' }
  | { kind: 'company'; section: 'operations' | 'competition' };

export interface StrategicAlert {
  id: string;
  tone: 'negative' | 'neutral' | 'positive';
  title: string;
  detail: string;
  target: StrategicAlertTarget;
}

const activeRoutes = (state: GameState) => state.routes.filter((route) => route.active);
const recentReports = (state: GameState, weeks: number) => state.reports.slice(-weeks);
const totalDebt = (state: GameState) => state.loans.reduce((sum, loan) => sum + loan.principalRemaining, 0);
const netAssets = (state: GameState) => state.cash + state.aircraft.reduce((sum, aircraft) => sum + aircraft.bookValue, 0) - totalDebt(state);

export const MILESTONES: MilestoneDefinition[] = [
  { id: 'first-flight', title: '首班航班', detail: '完成第一周航班结算', achieved: (state) => state.reports.length >= 1 },
  { id: 'first-profitable-quarter', title: '首次季度盈利', detail: '最近 13 周净利润为正', achieved: (state) => state.reports.slice(-13).reduce((sum, report) => sum + report.netProfit, 0) > 0 },
  { id: 'profitable-year', title: '年度盈利', detail: '最近 52 周累计净利润为正', achieved: (state) => state.reports.length >= 52 && recentReports(state, 52).reduce((sum, report) => sum + report.netProfit, 0) > 0 },
  { id: 'fleet-10', title: '机队达到 10 架', detail: '拥有或租赁 10 架飞机', achieved: (state) => state.aircraft.length >= 10 },
  { id: 'fleet-25', title: '中型机队', detail: '拥有或租赁 25 架飞机', achieved: (state) => state.aircraft.length >= 25 },
  { id: 'fleet-50', title: '机队达到 50 架', detail: '拥有或租赁 50 架飞机', achieved: (state) => state.aircraft.length >= 50 },
  { id: 'fleet-100', title: '机队达到 100 架', detail: '拥有或租赁 100 架飞机', achieved: (state) => state.aircraft.length >= 100 },
  { id: 'fleet-200', title: '超级机队', detail: '拥有或租赁 200 架飞机', achieved: (state) => state.aircraft.length >= 200 },
  { id: 'routes-10', title: '航线达到 10 条', detail: '运营 10 条活跃航线', achieved: (state) => activeRoutes(state).length >= 10 },
  { id: 'routes-25', title: '航线网络成形', detail: '运营 25 条活跃航线', achieved: (state) => activeRoutes(state).length >= 25 },
  { id: 'routes-50', title: '航线达到 50 条', detail: '运营 50 条活跃航线', achieved: (state) => activeRoutes(state).length >= 50 },
  { id: 'routes-100', title: '百线网络', detail: '运营 100 条活跃航线', achieved: (state) => activeRoutes(state).length >= 100 },
  { id: 'hubs-3', title: '三枢纽体系', detail: '建设 3 座枢纽', achieved: (state) => state.hubs.length >= 3 },
  { id: 'first-international-hub', title: '首个国际枢纽', detail: '香港以外地区建设枢纽', achieved: (state) => state.hubs.some((hub) => hub.airportId !== state.headquartersId) },
  { id: 'hubs-8', title: '多中心航空网', detail: '建设 8 座枢纽', achieved: (state) => state.hubs.length >= 8 },
  { id: 'passengers-100k', title: '十万旅客', detail: '累计服务 10 万名独立旅客', achieved: (state) => (state.careerRecords?.totalPassengers ?? 0) >= 100_000 },
  { id: 'passengers-10m', title: '千万旅客', detail: '累计服务 1,000 万名独立旅客', achieved: (state) => (state.careerRecords?.totalPassengers ?? 0) >= 10_000_000 },
  { id: 'reputation-80', title: '行业口碑', detail: '公司声誉达到 80', achieved: (state) => state.reputation >= 80 },
  { id: 'reputation-95', title: '传奇声誉', detail: '公司声誉达到 95', achieved: (state) => state.reputation >= 95 },
  { id: 'cash-75m', title: '现金安全垫', detail: '现金储备达到 7,500 万', achieved: (state) => state.cash >= 75_000_000 },
  { id: 'cash-250m', title: '资本实力', detail: '现金储备达到 2.5 亿', achieved: (state) => state.cash >= 250_000_000 },
  { id: 'net-assets-100m', title: '净资产过亿', detail: '公司净资产达到 1 亿', achieved: (state) => netAssets(state) >= 100_000_000 },
  { id: 'stable-load-factor', title: '高上座率运营', detail: '连续 13 周平均客座率达到 75%', achieved: (state) => {
    const reports = recentReports(state, 13);
    return reports.length >= 13 && reports.reduce((sum, report) => sum + report.averageLoadFactor, 0) / reports.length >= 0.75;
  } },
  { id: 'ipo', title: '完成上市', detail: '公司完成 IPO', achieved: (state) => state.companyListing.listed },
  { id: 'debt-free-year', title: '无负债经营年度', detail: '运营 52 周且无有息贷款', achieved: (state) => state.week >= 52 && totalDebt(state) <= 1 },
];

export function companyScore(state: GameState): CompanyScore {
  const recent = state.reports.slice(-52);
  const revenue = recent.reduce((sum, report) => sum + report.revenue, 0);
  const netProfit = recent.reduce((sum, report) => sum + report.netProfit, 0);
  const margin = revenue > 0 ? netProfit / revenue : 0;
  const activeRoutes = state.routes.filter((route) => route.active);
  const debt = state.loans.reduce((sum, loan) => sum + loan.principalRemaining, 0);
  const aircraftAssets = state.aircraft.reduce((sum, aircraft) => sum + aircraft.bookValue, 0);
  const assets = Math.max(1, state.cash + aircraftAssets);
  const avgLoad = recent.length ? recent.reduce((sum, report) => sum + report.averageLoadFactor, 0) / recent.length : 0;
  const profitability = Math.max(0, Math.min(25, Math.round((margin + 0.08) * 120)));
  const network = Math.min(25, Math.round(activeRoutes.length * 0.32 + state.hubs.length * 2.4 + state.aircraft.length * 0.12));
  const reputation = Math.min(20, Math.round(state.reputation / 5));
  const balanceSheet = Math.max(0, Math.min(15, Math.round((1 - debt / assets) * 15)));
  const resilience = Math.max(0, Math.min(15, Math.round((avgLoad * 8) + Math.min(7, state.cash / 20_000_000))));
  return {
    total: profitability + network + reputation + balanceSheet + resilience,
    profitability,
    network,
    reputation,
    balanceSheet,
    resilience,
  };
}

export function nextMilestone(state: GameState): MilestoneDefinition | undefined {
  const achieved = new Set(state.milestones?.achievedIds ?? []);
  return MILESTONES.find((milestone) => !achieved.has(milestone.id));
}

export function updateMilestones(state: GameState): GameState {
  const next = structuredClone(state);
  const achieved = new Set(next.milestones?.achievedIds ?? []);
  for (const milestone of MILESTONES) {
    if (achieved.has(milestone.id) || !milestone.achieved(next)) continue;
    achieved.add(milestone.id);
    next.events = [
      ...(next.events ?? []),
      {
        id: `event-${next.nextId++}`,
        week: next.week,
        tone: 'positive' as const,
        title: `里程碑达成：${milestone.title}`,
        detail: milestone.detail,
      },
    ].slice(-36);
  }
  next.milestones = { achievedIds: [...achieved], lastCheckedWeek: next.week };
  return next;
}

function buildMandate(state: GameState): ShareholderMandate {
  const seed = Math.abs(Math.floor(state.seed + state.week + (state.shareholderMandate?.target ?? 0))) % 5;
  const routeCount = state.routes.filter((route) => route.active).length;
  const debt = state.loans.reduce((sum, loan) => sum + loan.principalRemaining, 0);
  const baselineMargin = state.lastReport && state.lastReport.revenue > 0 ? state.lastReport.netProfit / state.lastReport.revenue : 0;
  const templates: Array<Omit<ShareholderMandate, 'id' | 'startWeek' | 'dueWeek' | 'status'>> = [
    { kind: 'margin', title: '提升利润率', detail: '52 周到期时净利率达到目标', target: Math.max(0.08, baselineMargin + 0.025), baseline: baselineMargin },
    { kind: 'route_growth', title: '扩大航线网络', detail: '52 周内增加活跃航线', target: routeCount + 8, baseline: routeCount },
    { kind: 'debt_reduction', title: '压降有息负债', detail: '52 周内降低总债务', target: debt * 0.88, baseline: debt },
    { kind: 'reputation', title: '守住品牌声誉', detail: '52 周到期时声誉不低于目标', target: Math.max(70, state.reputation), baseline: state.reputation },
    { kind: 'hub_growth', title: '强化枢纽网络', detail: '52 周内增加或升级枢纽', target: state.hubs.reduce((sum, hub) => sum + hub.level, 0) + 2, baseline: state.hubs.reduce((sum, hub) => sum + hub.level, 0) },
  ];
  const selected = templates[seed];
  return {
    ...selected,
    id: `mandate-${state.week}-${seed}`,
    startWeek: state.week,
    dueWeek: state.week + 52,
    status: 'active',
  };
}

function mandateMet(state: GameState, mandate: ShareholderMandate): boolean {
  const recentRevenue = state.reports.slice(-52).reduce((sum, report) => sum + report.revenue, 0);
  const recentProfit = state.reports.slice(-52).reduce((sum, report) => sum + report.netProfit, 0);
  const debt = state.loans.reduce((sum, loan) => sum + loan.principalRemaining, 0);
  switch (mandate.kind) {
    case 'margin':
      return recentRevenue > 0 && recentProfit / recentRevenue >= mandate.target;
    case 'route_growth':
      return state.routes.filter((route) => route.active).length >= mandate.target;
    case 'debt_reduction':
      return debt <= mandate.target;
    case 'reputation':
      return state.reputation >= mandate.target;
    case 'hub_growth':
      return state.hubs.reduce((sum, hub) => sum + hub.level, 0) >= mandate.target;
  }
}

export function updateShareholderMandate(state: GameState): GameState {
  if (!state.companyListing.listed) return state;
  const next = structuredClone(state);
  const current = next.shareholderMandate;
  if (!current || current.status !== 'active') {
    next.shareholderMandate = buildMandate(next);
    return next;
  }
  if (next.week < current.dueWeek) return next;
  const met = mandateMet(next, current);
  next.shareholderMandate = { ...current, status: met ? 'met' : 'missed' };
  next.events = [
    ...(next.events ?? []),
    {
      id: `event-${next.nextId++}`,
      week: next.week,
      tone: met ? 'positive' as const : 'negative' as const,
      title: met ? '股东目标达成' : '股东目标落空',
      detail: current.title,
    },
  ].slice(-36);
  if (!met && next.companyListing.listed) {
    next.companyListing.sharePrice = Math.max(0.1, next.companyListing.sharePrice * 0.94);
  }
  next.shareholderMandate = buildMandate(next);
  return next;
}

export function updateVictories(state: GameState): GameState {
  const next = structuredClone(state);
  const existing = new Set((next.victories ?? []).map((victory) => victory.id));
  const score = companyScore(next).total;
  const definitions: Array<Omit<VictoryRecord, 'week' | 'score'>> = [
    { id: 'regional-champion', title: '区域霸主' },
    { id: 'global-network', title: '全球航线网络' },
    { id: 'investor-darling', title: '资本宠儿' },
    { id: 'lowcost-giant', title: '廉航巨头' },
    { id: 'premium-leader', title: '高端服务龙头' },
  ];
  const qualifies = new Map<string, boolean>([
    ['regional-champion', next.routes.filter((route) => route.active).length >= 40 && next.reputation >= 75],
    ['global-network', next.hubs.length >= 6 && next.routes.filter((route) => route.active).length >= 80],
    ['investor-darling', next.companyListing.listed && score >= 82],
    ['lowcost-giant', next.routes.filter((route) => route.active).length >= 50 && next.reputation >= 70 && next.aircraft.length > 0 && next.aircraft.reduce((sum, aircraft) => sum + (aircraft.cabinConfig?.economy ?? 1), 0) / Math.max(1, next.aircraft.length) >= 0.9 && next.reports.slice(-26).every((report) => report.operatingProfit > 0)],
    ['premium-leader', next.routes.filter((route) => route.active).length >= 30 && next.reputation >= 92 && next.reports.slice(-26).every((report) => report.operatingProfit > 0) && next.routes.filter((route) => route.active).reduce((sum, route) => sum + route.stats.cabins.business.revenue + route.stats.cabins.first.revenue, 0) >= next.routes.filter((route) => route.active).reduce((sum, route) => sum + route.stats.revenue, 0) * 0.28],
  ]);
  for (const victory of definitions) {
    if (existing.has(victory.id) || !qualifies.get(victory.id)) continue;
    next.victories = [...(next.victories ?? []), { ...victory, week: next.week, score }];
    next.events = [
      ...(next.events ?? []),
      {
        id: `event-${next.nextId++}`,
        week: next.week,
        tone: 'positive' as const,
        title: `胜利目标达成：${victory.title}`,
        detail: `公司综合评分 ${score}，可继续沙盒经营。`,
      },
    ].slice(-36);
  }
  return next;
}

export function updateObjectives(state: GameState): GameState {
  return updateAchievements(updateVictories(updateShareholderMandate(updateMilestones(state))));
}

export function strategicAlerts(state: GameState): StrategicAlert[] {
  const alerts: StrategicAlert[] = [];
  const activeRoutes = state.routes.filter((route) => route.active);
  const lowMarginRoutes = activeRoutes.filter((route) => route.stats.operatingProfit < 0 && route.maturityWeeks >= 8);
  if (lowMarginRoutes.length) {
    alerts.push({
      id: 'low-margin-routes',
      tone: 'negative',
      title: '成熟亏损航线',
      detail: `${lowMarginRoutes.length} 条成熟航线正在亏损，优先检查票价、运力和机型。`,
      target: { kind: 'route', routeId: lowMarginRoutes[0].id },
    });
  }
  const dueDebt = state.loans.filter((loan) => loan.kind === 'bank' && loan.weeksRemaining <= 8).reduce((sum, loan) => sum + loan.principalRemaining, 0);
  if (dueDebt > 0) {
    alerts.push({
      id: 'debt-due',
      tone: 'negative',
      title: '银行贷款临近到期',
      detail: `未来 8 周内需处理 ${Math.round(dueDebt).toLocaleString('zh-CN')} 债务。`,
      target: { kind: 'finance', tab: 'debt' },
    });
  }
  const underusedHubs = state.hubs.filter((hub) => activeRoutes.filter((route) => [route.originId, route.destinationId].includes(hub.airportId)).length < Math.max(2, hub.level * 2));
  if (underusedHubs.length) {
    alerts.push({
      id: 'underused-hubs',
      tone: 'neutral',
      title: '枢纽利用不足',
      detail: `${underusedHubs.map((hub) => hub.airportId).join('、')} 的航线密度不足以支撑当前等级。`,
      target: { kind: 'company', section: 'operations' },
    });
  }
  const lowCrewMood = state.aircraft.filter((aircraft) => aircraft.routeIds.length && aircraft.crewMood < 35);
  if (lowCrewMood.length) {
    alerts.push({
      id: 'crew-mood',
      tone: 'negative',
      title: '机组情绪偏低',
      detail: `${lowCrewMood.length} 架执飞飞机存在罢工或离职风险。`,
      target: { kind: 'aircraft', aircraftId: lowCrewMood[0].id },
    });
  }
  const competitorPressure = (state.aiManagers ?? [])
    .filter((manager) => !manager.bankrupt)
    .reduce((sum, manager) => sum + manager.routes.filter((route) => route.active && state.routes.some((playerRoute) => playerRoute.active && [playerRoute.originId, playerRoute.destinationId].sort().join('-') === [route.originId, route.destinationId].sort().join('-'))).length, 0);
  if (competitorPressure) {
    alerts.push({
      id: 'competitor-pressure',
      tone: 'neutral',
      title: '直接竞争航线',
      detail: `${competitorPressure} 条对手航线与你的网络重叠，关注价格战与份额变化。`,
      target: { kind: 'company', section: 'competition' },
    });
  }
  const surplusAircraft = state.aircraft.filter((aircraft) => aircraft.routeIds.length === 0 && (state.week - aircraft.acquiredWeek) >= 8);
  if (surplusAircraft.length) {
    alerts.push({ id: 'capacity-surplus', tone: 'neutral', title: '闲置运力积压', detail: `${surplusAircraft.length} 架飞机连续 8 周未执飞，检查是否需要退租、出售或重新配线。`, target: { kind: 'aircraft', aircraftId: surplusAircraft[0].id } });
  }
  const oldInefficientAircraft = state.aircraft.filter((aircraft) => aircraft.condition < 0.65 && aircraft.weeksOwned >= 104 && aircraft.routeIds.length > 0);
  if (oldInefficientAircraft.length) {
    alerts.push({ id: 'old-aircraft', tone: 'negative', title: '高龄低效率飞机', detail: `${oldInefficientAircraft.length} 架飞机机况偏低且运营超过 2 年，比较维护与替换成本。`, target: { kind: 'aircraft', aircraftId: oldInefficientAircraft[0].id } });
  }
  if (!alerts.length && state.routes.length) {
    alerts.push({
      id: 'stable',
      tone: 'positive',
      title: '经营面平稳',
      detail: '暂无高优先级风险，适合规划下一轮扩张或枢纽升级。',
      target: { kind: 'company', section: 'operations' },
    });
  }
  return alerts.slice(0, 5);
}
